import OutlinedInput, { OutlinedInputProps } from "@mui/material/OutlinedInput";
import { useWatchDependency } from "@sinch/hooks/utilities/useWatchDependency";
import { isNil } from "ramda";
import React, { FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormControlWrapper, InputWrapperProps } from "@sinch/components/form/FormControlWrapper/FormControlWrapper";

/**
 * A duration as it is allowed to look while being typed: hours, an optional colon and up to two minute digits
 */
const DURATION_PATTERN = /^\d{0,4}:?\d{0,2}$/;

/**
 * Format whole minutes as `H:MM`
 */
export const formatDurationMinutes = (minutes: number | null | undefined): string =>
  isNil(minutes) ? "" : `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}`;

/**
 * Parse a `H:MM` duration into whole minutes. Input without a colon is read as minutes,
 * so both `90` and `1:30` give an hour and a half. Returns null for a blank input.
 */
export const parseDurationMinutes = (text: string): number | null => {
  const trimmed = text.trim();
  if (trimmed === "" || trimmed === ":") {
    return null;
  }
  const [hours, minutes] = trimmed.includes(":") ? trimmed.split(":") : ["0", trimmed];
  const totalMinutes =
    Math.round(Number(hours === "" ? 0 : hours)) * 60 + Math.round(Number(minutes === "" ? 0 : minutes));

  return Number.isFinite(totalMinutes) ? totalMinutes : null;
};

export type DurationInputProps = Omit<OutlinedInputProps, "value" | "onChange" | "type"> &
  Omit<InputWrapperProps, "slots" | "slotProps"> & {
    /**
     * Duration in whole minutes
     */
    value?: number | null;
    /**
     * Called with the entered duration in whole minutes
     */
    onChange?: (value: number | null) => void;
    /**
     * Shortest duration the input accepts, in whole minutes
     * @default 0
     */
    min?: number;
  };

/**
 * Duration input typed and displayed as `H:MM`, holding the value as whole minutes so that
 * no amount of typing can produce a fractional one. Shows an info icon explaining the
 * accepted format unless `infoTooltip` overrides it
 */
export const DurationInput: FC<DurationInputProps> = ({
  label,
  FormHelperTextProps: formHelperTextProps,
  FormControlProps: formControlProps,
  helperText,
  errorMessage,
  error,
  required,
  infoTooltip,
  fullWidth,
  value,
  onChange,
  onBlur,
  onKeyDown,
  min = 0,
  ...props
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState<string>(formatDurationMinutes(value));
  useWatchDependency(() => {
    setText(formatDurationMinutes(value));
  }, [value]);

  const commit = useCallback(() => {
    const minutes = parseDurationMinutes(text);
    const accepted = isNil(minutes) ? null : Math.max(minutes, min);
    setText(formatDurationMinutes(accepted));
    onChange?.(accepted);
  }, [text, min, onChange]);

  return (
    <FormControlWrapper
      error={error}
      errorMessage={errorMessage}
      FormHelperTextProps={formHelperTextProps}
      fullWidth={fullWidth}
      helperText={helperText}
      infoTooltip={infoTooltip ?? t("Form.durationFormatHint")}
      label={label}
      required={required}
      {...formControlProps}
    >
      <OutlinedInput
        {...props}
        error={error}
        fullWidth={fullWidth}
        onBlur={(event) => {
          commit();
          onBlur?.(event);
        }}
        onChange={(event) => {
          if (DURATION_PATTERN.test(event.target.value)) {
            setText(event.target.value);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
          }
          onKeyDown?.(event);
        }}
        placeholder={props.placeholder ?? "0:00"}
        required={required}
        value={text}
      />
    </FormControlWrapper>
  );
};
